import {
  Alert,
  Box,
  Button,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { useMemo, useState } from "react"
import {
  suggestElementType,
  GPT_5,
  GPT_5_2,
} from "../pages/services/chatGenerationServices"
import {
  createContent,
  getReferencedPromptViews,
} from "../pages/services/createContentServices"
import { useSnackbar } from "notistack"
import db from "../Firestore"
import { useEffect } from "react"
import {
  getLeafNodesOfElement,
  getLeafNodesOfView,
} from "../pages/services/modelEditServices"
import * as modelServices from "../pages/services/modelServices"
import * as palette from "./symbols/palette"
import LevelSpec from "./LevelSpec"
import { selectModelState, selectElementDefinitions } from "../redux/selectors"
import { setModelState } from "../redux/actions"
import { useSelector, useDispatch } from "react-redux"
import GPTModelSelect from "./GPTModelSelect"
import * as chatGenerationServices from "../pages/services/chatGenerationServices"
import ModelEditViewSetFiles from "./ModelEditViewSetFiles"

const ModelEditCreateContent = (props) => {
  const {
    accountId,
    selectedViewId,
    viewSet,
    handlePasteAdd,
    setTextResponse,
    // Optional selected element, otherwise generated elements are for top level of view
    currentElement,
    currentView,
    setWaitingElementIds,
    generationPanelCount,
    setGenerationPanelCount,
    stopRequested,
    setGeneratingContentMessage,
    handleUpdatePromptHistory,
    handleGenerateDescription,
    views,
    roles,
    defaultLevelSpecs,
  } = props

  const modelCache = useSelector(selectModelState)

  const elementDefinitions = useSelector(selectElementDefinitions)

  const dispatch = useDispatch()

  const [mode, setMode] = useState("basic")

  const [selectedGPTModel, setSelectedGPTModel] = useState(GPT_5_2)

  const [loadedViewsCache, setLoadedViewsCache] = useState([])

  const [prompts, setPrompts] = useState([])

  const [vectorStores, setVectorStores] = useState([])

  const [vectorStoreOptions, setVectorStoreOptions] = useState([])

  const [viewSetFiles, setViewSetFiles] = useState([])

  // A cache of view refs that we've already resolved, as the user is editing the prompt
  // Each entry in the array has a src, i.e. the [<viewref>] string, and a resolvedViewRef, which is the view object
  const [resolvedViewRefs, setResolvedViewRefs] = useState([])

  // Can be 'selected', or 'leaf' -- which is where content gets generated from
  const [attachTo, setAttachTo] = useState("leaf")

  const [selectedPromptId, setSelectedPromptId] = useState("")

  const [useElements, setUseElements] = useState(true)

  const { enqueueSnackbar } = useSnackbar()

  const BLANK_LEVEL_SPEC = {
    type: "", // palette.getIndex(palette.CAPABILITY),
    levels: 1,
    info: "",
    attrs: ["name"],
    qty: 10,
    qty_selection: chatGenerationServices.QTY_SELECTION_ANY,
    auto_qty: false,
    props: [],
    max_words: 25,
  }

  const [levelSpecs, setLevelSpecs] = useState([
    {
      ...BLANK_LEVEL_SPEC,
      seq: 1,
      props: [],
    },
  ])

  useEffect(() => {
    if (currentView) {
      setAttachTo(currentView.elements?.length === 0 ? "top" : "leaf")
    }
  }, [currentView])

  useEffect(() => {
    if (accountId) {
      db.collection("vector_stores")
        .where("account_id", "==", accountId)
        .get()
        .then((querySnapshot) => {
          const options = []

          querySnapshot.forEach((doc) => {
            const data = doc.data()
            options.push({ id: doc.id, label: data.name, vs_id: data.vs_id })
          })
          setVectorStoreOptions(options.filter((item) => item.vs_id))
        })
    }
  }, [accountId])

  useEffect(() => {
    if (defaultLevelSpecs) {
      setLevelSpecs(defaultLevelSpecs)
    }
  }, [defaultLevelSpecs])

  const createModelCacheEntry = (model, fileName, parentId, type, name) => {
    const modelState = modelServices.createModelCacheItem(
      model,
      fileName,
      name,
      parentId,
      type
    )

    return modelState
  }

  // Parse the prompt and ensure any project or component references are loaded into modelCache
  const handleEnsureModelIsLoaded = async (prompt) => {
    const { loadedViews, missingViews } = await getReferencedPromptViews({
      accountId: accountId,
      prompt: prompt,
      views: views,
      modelCache: modelCache,
      resolvedViewRefs: resolvedViewRefs,
      loadedViewsCache: loadedViewsCache,
    })

    setLoadedViewsCache(loadedViews)

    if (missingViews.length === 0) {
      return
    }

    // Now load all the missing refs into cache
    for (const [index, ref] of missingViews.entries()) {
      // This callback defined in the for loop so we can reference attributes of the ref variable
      const loadModelIntoCache = (model, fileName, rawText, props) => {
        // Add a new cache item into the cache
        const modelState = createModelCacheEntry(
          model,
          ref.file,
          ref.id,
          ref.type,
          model.name
        )

        // Replace the model cache with the updated state, incl. the newly added item
        dispatch(setModelState(modelState))
      }

      if (["project", "component"].includes(ref.type)) {
        const pathType = ref.type === "project" ? "projects" : "components"
        const filePath = `accounts/${accountId}/${pathType}/${ref.id}/`
        await modelServices.loadFile(
          filePath,
          ref.file,
          // Calls the above callback to add the model to the cache in Redux
          loadModelIntoCache,
          {
            createModelIndex: false,
          },
          false
        )
      }
    }
  }

  const handleClickCreateContent = async ({ roles }) => {
    if (!currentView) {
      console.log("no current view")
      return
    }

    // Check all level specs have a type
    if (useElements && levelSpecs.some((levelSpec, index) => !levelSpec.type)) {
      enqueueSnackbar(`Please select an element type`, { variant: "warning" })
      return
    }

    const maxWords =
      chatGenerationServices.getAIUsageParam({
        roles,
        paramName: chatGenerationServices.MAX_DESCRIPTION_WORDS,
      }) || 50

    if (
      useElements &&
      levelSpecs.some((levelSpec) => levelSpec.max_words > maxWords)
    ) {
      enqueueSnackbar(
        `Max words for a description is ${maxWords}. Please reduce the number of words in your prompt`,
        { variant: "warning" }
      )
      return
    }

    // Check qty is <= 30 for 1st level, and <= 10 for 2nd and subsequent levels
    const firstLevelSpec = levelSpecs[0]
    const restLevelSpecs = levelSpecs.slice(1)

    if (firstLevelSpec.qty > 50) {
      enqueueSnackbar("Max qty for 1st level is 50", { variant: "warning" })
      return
    }

    if (restLevelSpecs.some((levelSpec) => levelSpec.qty > 10)) {
      enqueueSnackbar("Max qty for 2nd and subsequent levels is 10", {
        variant: "warning",
      })
      return
    }

    const maxElementId = currentView.elements.reduce((maxId, element) => {
      if (element.id > maxId) {
        return element.id
      } else {
        return maxId
      }
    }, 0)

    stopRequested.value = false
    setGenerationPanelCount((curr) => curr + 1)

    const createContentProps = {
      accountId: accountId,
      modelCache: modelCache,
      viewSet,
      views,
      currentView: currentView,
      maxElementId: maxElementId,
      levelSpecs: levelSpecs,
      elementDefinitions: elementDefinitions,
      handlePasteAdd,
      setWaitingElementIds,
      setGeneratingContentMessage,
      gptModel: selectedGPTModel,
      roles: roles,
      //vectorStores: vectorStores,
      viewSetFiles: viewSetFiles,
      setTextResponse,
      useElements,
      alignElements: true,
    }

    console.log("createContentProps", { createContentProps, attachTo })

    switch (attachTo) {
      case "top":
        const result = await createContent({
          ...createContentProps,
          stopRequested,
          currentElement: undefined,
        })
        if (result.error) {
          enqueueSnackbar(result.error, { variant: "error" })
          return
        }
        break

      case "selected":
        const resultSelected = await createContent({
          ...createContentProps,
          stopRequested,
          currentElement: currentElement,
        })
        if (resultSelected.error) {
          enqueueSnackbar(resultSelected.error, { variant: "error" })
          return
        }
        break

      case "leaf":
        const leafNodes = currentElement
          ? getLeafNodesOfElement(currentElement, currentView)
          : getLeafNodesOfView(currentView)
        let currentMaxId = maxElementId

        console.log("leaf nodes", leafNodes)

        let pastedElements

        for (const [index, leafNode] of leafNodes.entries()) {
          if (stopRequested.value === true) {
            console.log("stop requested")
            break
          }

          pastedElements = await createContent({
            ...createContentProps,
            stopRequested,
            currentElement: leafNode,
            currentView: {
              ...currentView,
              elements: index === 0 ? currentView.elements : pastedElements,
            },
            maxElementId: currentMaxId,
          })
          // If we clicked stop, then pastedElements will be undefined
          console.log("pastedElements", { pastedElements })
          if (pastedElements) {
            currentMaxId = pastedElements.reduce((maxId, element) => {
              if (element.id > maxId) {
                return element.id
              } else {
                return maxId
              }
            }, 0)
            console.log(
              "%cafter top loop",
              "color:yellow",
              {
                currentMaxId,
                pastedElements,
                ids: pastedElements.map((element) => element.id).join(", "),
              },
              leafNode.name
            )
          }
        }
        break

      default:
        console.log('attachTo not "top", "selected", or "leaf"')
        break
    }

    setWaitingElementIds([])
    setGeneratingContentMessage("")
    setGenerationPanelCount((curr) => curr - 1)
    handleUpdatePromptHistory({ levelSpecs })
    //handleGenerateDescription({ currentView, viewSet, roles })
  }

  const parentElements = useMemo(() => {
    if (!currentView) {
      return
    }

    const result = [
      {
        value: "top",
        toggleButton: (
          <ToggleButton key="top" value="top">
            Top
          </ToggleButton>
        ),
        message: (
          <Typography variant="caption">
            Attach to the top level of the view
          </Typography>
        ),
        // No parents. Elements to be generated will be top level and not attached to any parent(s)
        parents: undefined,
        showParents: false,
      },
    ]

    if (currentElement) {
      result.push({
        value: "selected",
        toggleButton: (
          <ToggleButton key="selected" value="selected">
            Selected
          </ToggleButton>
        ),
        message: (
          <Typography variant="caption">
            Attach as child elements under <b>{currentElement.name}</b>
          </Typography>
        ),
        parents: [currentElement],
        showParents: false,
      })
    }

    if (currentElement) {
      result.push({
        value: "leaf",
        toggleButton: (
          <ToggleButton key="leaf" value="leaf">
            Leaf
          </ToggleButton>
        ),
        message: (
          <Typography variant="caption">
            Content will be attached to the leaf elements of{" "}
            <b>{currentElement.name}</b>
          </Typography>
        ),
        parents: getLeafNodesOfElement(currentElement, currentView),
        showParents: true,
      })
    } else {
      result.push({
        value: "leaf",
        toggleButton: (
          <ToggleButton key="leaf" value="leaf">
            Leaf
          </ToggleButton>
        ),
        message: (
          <Typography variant="caption">
            Attach content to leaf elements
          </Typography>
        ),
        parents: getLeafNodesOfView(currentView),
      })
    }

    return result
  }, [currentElement, currentView])

  useEffect(() => {
    if (accountId) {
      db.collection("prompts")
        .where("account_id", "==", accountId)
        .get()
        .then((querySnapshot) => {
          const newPrompts = []
          querySnapshot.forEach((doc) => {
            newPrompts.push({ id: doc.id, ...doc.data() })
          })
          setPrompts(newPrompts)
        })
    }
  }, [accountId])

  useEffect(() => {
    if (currentElement) {
      const firstLevelSpec = levelSpecs[0]
      const restLevelSpecs = levelSpecs.slice(1)

      setLevelSpecs([
        { ...firstLevelSpec, type: currentElement.type },
        ...restLevelSpecs,
      ])
    }
  }, [currentElement])

  const handleSuggestElementType = async (levelSpec) => {
    const suggestedElementType = await suggestElementType({
      purpose: viewSet.purpose,
      prompt: levelSpec.info,
    })

    console.log("%csuggestElementType", "color:blue", { suggestedElementType })

    const elementType = palette.getElementType(
      suggestedElementType.element_type
    )
    if (!elementType) {
      console.warn("Unknown element type:", suggestedElementType.element_type)
      enqueueSnackbar(
        `Unknown element type: ${suggestedElementType.element_type}`,
        {
          variant: "warning",
        }
      )
      return null
    }

    suggestedElementType.id = elementType.index
    return suggestedElementType
  }

  return (
    <>
      <Stack gap={2}>
        <Box>
          <EditMode mode={mode} setMode={setMode} />
        </Box>
        <Box sx={{ marginBottom: "10px" }}>
          {levelSpecs.length === 0 && <NoLevelSpecsAlert />}

          <Stack>
            {prompts.length === 0 && (
              <Typography variant="caption" color="text.primary">
                None found. Use the 'Prompts' menu.
              </Typography>
            )}
          </Stack>

          {mode === "extended" && (
            <Box sx={{ mt: "10px", padding: 1, width: "400px" }}>
              <GPTModelSelect
                selectedGPTModel={selectedGPTModel}
                setSelectedGPTModel={setSelectedGPTModel}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {currentView &&
              levelSpecs.map((levelSpec, index) => (
                <Box key={levelSpec.seq}>
                  <LevelSpec
                    key={levelSpec.seq}
                    index={index}
                    accountId={accountId}
                    mode={mode}
                    modelCache={modelCache}
                    levelSpec={levelSpec}
                    levelSpecCount={levelSpecs.length}
                    setLevelSpec={(newLevelSpec) => {
                      setLevelSpecs((curr) => {
                        const newSpecs = [...curr]
                        newSpecs[index] = newLevelSpec
                        return newSpecs
                      })
                    }}
                    handleSuggestElementType={handleSuggestElementType}
                    handleEnsureModelIsLoaded={handleEnsureModelIsLoaded}
                    currentElement={currentElement}
                    viewSet={viewSet}
                    views={views}
                    roles={roles}
                    useElements={useElements}
                  />

                  {mode === "extended" && (
                    <Stack sx={{ marginTop: "10px", maxWidth: "300px" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        Add view reference to Level {index + 1} prompt
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select a view name below, which copies a reference to
                        that view into your prompt above. This allows your
                        prompt you are writing to refer to content in other
                        views.
                      </Typography>
                      <Stack
                        direction="column"
                        gap={0}
                        sx={{ marginTop: "10px" }}
                      >
                        {views &&
                          views.map((view) => (
                            <MenuItem
                              key={view.id}
                              sx={{ padding: "3px", paddingLeft: "10px" }}
                              onClick={(e) => {
                                setLevelSpecs((curr) => {
                                  const newSpecs = [...curr]
                                  // Append the view name to the current levelSpec, wrapped in [ and ] to indicate it's a view name
                                  const formattedViewName = `[design:${viewSet.name}:${view.name}]`

                                  newSpecs[
                                    index
                                  ].info = `${newSpecs[index].info}${formattedViewName}`
                                  return newSpecs
                                })
                              }}
                            >
                              <Typography variant="caption">
                                {view.name}
                              </Typography>
                            </MenuItem>
                          ))}
                      </Stack>
                    </Stack>
                  )}
                </Box>
              ))}
          </Box>
        </Box>

        <Divider />

        <Stack gap={1} sx={{ marginTop: "10px" }}>
          {mode === "extended" && (
            <>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Add Content To
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Select where to attach the generated content
                </Typography>
              </Box>
              <ToggleButtonGroup
                color="primary"
                value={attachTo}
                exclusive
                size="small"
                onChange={(e) => setAttachTo(e.target.value)}
                aria-label="Attach To"
              >
                {parentElements &&
                  parentElements.map((item) => item.toggleButton)}
              </ToggleButtonGroup>

              <Box sx={{ maxWidth: "300" }}>
                {attachTo &&
                  parentElements.find((item) => item.value === attachTo)
                    ?.message}
              </Box>
            </>
          )}
        </Stack>

        <Box sx={{ width: "300px" }}>
          <ModelEditViewSetFiles
            label="Files to include in content creation"
            viewSet={viewSet}
            viewSetFiles={viewSetFiles}
            setViewSetFiles={(newValue) => {
              setViewSetFiles(newValue)

              // Remove and re-add any guidance on using files, which is in the form 'File Usage: ......\n'

              const levelSpec = levelSpecs[0]

              // Remove all text from levelSpec.info that starts with 'File Usage: ' and ends with either '\n' or the end of the string

              if (levelSpec) {
                const strippedInfo = levelSpec.info
                  .replace(/File Usage: .*?(\n|$)/, "")
                  .trim()

                console.log("strippedInfo", strippedInfo)

                const infoWithFileGuidance =
                  strippedInfo +
                  (newValue.length > 0
                    ? `\n\nFile Usage: Align to these files and use them for content as appropriate - ${newValue
                        .map((file) => file.label)
                        .join(", ")}\n`
                    : "")

                setLevelSpecs((curr) => {
                  const newSpecs = [...curr]
                  newSpecs[0].info = infoWithFileGuidance
                  return newSpecs
                })
              }
            }}
          />
        </Box>

        <Box sx={{ marginTop: "20px", display: "flex", marginLeft: "auto" }}>
          {generationPanelCount > 0 && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
          <Button
            variant="contained"
            disabled={
              selectedViewId === "" ||
              levelSpecs.length === 0 ||
              generationPanelCount > 0 ||
              levelSpecs[0]?.type === -1
            }
            onClick={() => {
              handleClickCreateContent({ roles })
            }}
            sx={{ textTransform: "none" }}
          >
            Generate Content
          </Button>
        </Box>
      </Stack>
    </>
  )
}

const EditMode = ({ mode, setMode }) => {
  return (
    <ToggleButtonGroup
      color="primary"
      size="small"
      value={mode}
      exclusive
      onChange={(e) => setMode(e.target.value)}
      aria-label="Mode"
    >
      <ToggleButton
        value="basic"
        sx={{ textTransform: "none", fontSize: "12px" }}
      >
        Basic
      </ToggleButton>
      <ToggleButton
        value="extended"
        sx={{ textTransform: "none", fontSize: "12px" }}
      >
        Extended
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

const NoLevelSpecsAlert = () => {
  return (
    <Box sx={{ width: "300px" }}>
      <Alert severity="info" sx={{ marginTop: "10px" }}>
        <Typography variant="caption">
          Please define at least one level spec. Click <b>Add Level Spec</b>{" "}
          below
        </Typography>
      </Alert>
    </Box>
  )
}

export default ModelEditCreateContent
